import type { Building } from "building-types/building/buildingTypes";
import type { BuildingGenerationIssue } from "building-types/settings/buildingGenerationIssue";
import type { CornerBoards } from "building-types/trim/cornerBoardTypes";
import type { Table } from "common/data/table";
import { makeCornerBoards } from "../elements/cornerBoards/cornerBoard";
import makeGenerationStageComparator from "../utils/makeGenerationStageComparator";
import memo from "../utils/memo";

function select({
  levels,
  settings,
  blocks,
  labels,
  planarGraphs,
  planarGraphPerimeters,
  roofSystems: roofs,
  rooms,
  roomLevelIndices,
  roomsByLevel,
  facades,
  walls,
  doc,
  enabledFeatures,
}: Pick<
  Building,
  | "levels"
  | "settings"
  | "blocks"
  | "labels"
  | "planarGraphs"
  | "planarGraphPerimeters"
  | "roofSystems"
  | "rooms"
  | "roomLevelIndices"
  | "roomsByLevel"
  | "facades"
  | "walls"
  | "doc"
  | "enabledFeatures"
>) {
  return {
    levels,
    blocks,
    labels,
    planarGraphs,
    planarGraphPerimeters,
    roofs,
    rooms,
    roomLevelIndices,
    roomsByLevel,
    facades,
    wallSystems: walls,
    exteriorTrimSettings: settings.exteriorTrim,
    profileSource: {
      settingsToCatalogs: enabledFeatures.settingsToCatalogs,
      profiles: doc.profiles,
      profileSettings: settings.profile,
    },
    wainscotingSettings: settings.wainscoting,
    wallSettings: settings.wall,
  };
}

const compare = makeGenerationStageComparator<typeof select>({ profileSource: "shallow" });

function generate({
  levels,
  blocks,
  labels,
  planarGraphs,
  planarGraphPerimeters,
  roofs,
  rooms,
  roomLevelIndices,
  roomsByLevel,
  facades,
  wallSystems,
  exteriorTrimSettings,
  profileSource,
  wainscotingSettings,
  wallSettings,
}: ReturnType<typeof select>): { cornerBoards: Table<CornerBoards>; issues: BuildingGenerationIssue[] } {
  const issues: BuildingGenerationIssue[] = [];
  const cornerBoards = makeCornerBoards({
    levels,
    blocks,
    labels,
    planarGraphs,
    planarGraphPerimeters,
    roofs,
    rooms,
    roomLevelIndices,
    roomsByLevel,
    facades,
    wallSystems,
    exteriorTrimSettings,
    profileSource,
    wainscotingSettings,
    wallSettings,
    issues,
  });

  return { cornerBoards, issues };
}

export default memo(select, generate, compare);
